import styled from 'styled-components';

export const FileViewerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 0;
    align-items: stretch;
  }
`;

export const FileViewerContainer = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  width: 100%;
  max-width: 1200px;
  max-height: 95vh;
  display: flex;
  flex-direction: column;
  position: relative;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);

  @media (max-width: 768px) {
    max-width: 100%;
    max-height: 100vh;
    border-radius: 0;
    margin: 0;
  }
`;

export const FileViewerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e5e7eb;
`;

export const FileViewerBody = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  position: relative;
  min-height: 500px;
  max-height: 80vh;
  overflow: hidden;

  @media (max-width: 768px) {
    min-height: 400px;
    max-height: 85vh;
  }
`;

export const FileViewerFooter = styled.div`
  background: #f8f9fa;
  border-top: 1px solid #e5e7eb;
  padding: 16px 20px;
`;

export const FilePreview = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const FileInfo = styled.div`
  flex: 1;
  color: #1f2937;
`;

export const FileTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
`;

export const FileDescription = styled.p`
  margin: 0 0 12px 0;
  font-size: 14px;
  color: #6b7280;
  line-height: 1.4;
`;

export const FileMeta = styled.div`
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #6b7280;

  span {
    display: flex;
    align-items: center;
    gap: 4px;
  }
`;

export const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

export const DownloadButton = styled.button`
  background: #10b981;
  border: none;
  color: white;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s ease;

  &:hover {
    background: #059669;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const CloseButton = styled.button`
  background: #ef4444;
  border: none;
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s ease;

  &:hover {
    background: #dc2626;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const FileIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: ${props => props.color ? `${props.color}15` : '#f3f4f6'};
  border-radius: 12px;
  color: ${props => props.color || '#6b7280'};
  font-size: 24px;
`;

export const FileSize = styled.span`
  font-weight: 500;
  color: #374151;
`;

export const FileType = styled.span`
  background: ${props => props.color ? `${props.color}15` : '#f3f4f6'};
  color: ${props => props.color || '#6b7280'};
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 500;
  font-size: 11px;
`;

export const PreviewContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  border-radius: 8px;
  overflow: hidden;
`;

export const DocumentPreviewContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
`;

export const UnsupportedFile = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px;
  color: #6b7280;

  h3 {
    margin: 16px 0 8px 0;
    font-size: 18px;
    font-weight: 600;
    color: #374151;
  }

  p {
    margin: 4px 0;
    font-size: 14px;
    line-height: 1.5;
  }
`;

export const LoadingSpinner = styled.div`
  width: ${props => props.size === 'small' ? '20px' : '32px'};
  height: ${props => props.size === 'small' ? '20px' : '32px'};
  border: 2px solid #e5e7eb;
  border-top: 2px solid #2c5f34;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Responsive styles
export const ResponsiveFileViewer = styled(FileViewerContainer)`
  @media (max-width: 768px) {
    margin: 10px;
    max-height: 85vh;
    
    ${FileViewerHeader} {
      padding: 16px;
    }
    
    ${FileTitle} {
      font-size: 16px;
    }
    
    ${FileDescription} {
      font-size: 13px;
    }
    
    ${ActionButtons} {
      flex-direction: column;
      gap: 8px;
    }
    
    ${DownloadButton}, ${CloseButton} {
      width: 100%;
      justify-content: center;
    }
  }
`;
